import { forwardRef, type LabelHTMLAttributes } from "react";
import styled from "@emotion/styled";

export type LabelProps = LabelHTMLAttributes<HTMLLabelElement> & {
  required?: boolean;
  disabled?: boolean;
};

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline.
 *
 * Cascade notes:
 * - `bfrs-leading-none` [pos 49527] sits AFTER `bfrs-text-xs` [pos 48728] in the
 *   compiled stylesheet, so line-height 1 wins over text-xs's 1rem — modeled as
 *   the net `lineHeight: 1`.
 * - When disabled, tailwind-merge dropped the earlier `bfrs-text-text-primary`
 *   in favor of `bfrs-text-text-disabled`, so the colors are exclusive. */

const LabelRoot = styled("label", {
  label: "AgenticLabel-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { disabled: boolean } }>(({ ownerState }) => [
  {
    fontSize: ".75rem",
    fontWeight: 600,
    lineHeight: 1
  },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
]);

const LabelRequiredMark = styled("span", {
  label: "AgenticLabel-requiredMark"
})({
  marginLeft: ".25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

export const Label = forwardRef<HTMLLabelElement, LabelProps>(({ className, children, required, disabled, ...props }, ref) => (
  <LabelRoot ref={ref} ownerState={{ disabled: Boolean(disabled) }} className={className} {...props}>
    {children}
    {required && <LabelRequiredMark aria-hidden="true">*</LabelRequiredMark>}
  </LabelRoot>
));

Label.displayName = "Label";
